import React, {useEffect, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { Breadcrumb, BackdropCircular } from 'components';
import { getDepositListApi } from 'api';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import { useStyles } from './styles';
import { defaultTableCells } from './helpers';
import DepositsTableToolbar from './DepositsTableToolbar';
import DepositsTableRow from './DepositsTableRow';
import DepositsFilterDrawer from './DepositsFilterDrawer';


interface ILocationStateType {
  order_id: string
}

const Deposits = () => {
  const classes = useStyles();
  const { state } = useLocation<ILocationStateType>();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    depositsList,
    isDepositsLoading,
    depositsTotalCount
  } = useSelector((state: IReduxState) => state.deposits);
  const { isDepositIntegrated, loadingDepositConfig } = useSelector((state: IReduxState) => state.store_configs);

  const [sortDirection, setSortDirection] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>({});
  const [searchConditions, setSearchConditions] = useState({});
  const { headTableCells, setHeadTableCells } = useLocalStorageTableCells('depositTableCells', defaultTableCells);
  const [selectedOrderId, setSelectedOrderId] = useState(state?.order_id);

  useEffect(() => {
    if (!isDepositIntegrated) {
      history.push('/');
    }
  }, []);

  useEffect(() => {
    if (selectedOrderId) {
      setSearchConditions({field: 'order_id', value: `%${selectedOrderId}%`, condition_type: 'like'});
      setSelectedOrderId('');
    }

    getDepositListApi({
      dispatch,
      sortDirection,
      sortField,
      pageSize: rowsPerPage,
      currentPage,
      filterConditions: Object.values(filterConditions)
        .filter((condition) => condition.value !== ''),
      searchConditions
    });
  }, [
    sortField,
    sortDirection,
    rowsPerPage,
    currentPage,
    filterConditions,
    searchConditions
  ]);

  const onSearch = (searchValue, searchField) => {
    const condition = {
      field: searchField,
      value: searchValue.length ? `%${searchValue}%` : '',
      conditionType: 'like'
    };
    setSearchConditions(searchValue.length ? condition : {});
    setCurrentPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = sortField === property && sortDirection === 'desc';
    setSortDirection(isDesc ? 'asc' : 'desc');
    setSortField(property);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Deposits'}
          ]}
        />
      </div>
      <div className={classes.card}>
        <div className={`py-4 ${classes.toolbarContainer}`}>
          <DepositsTableToolbar
            onSearch={onSearch}
            setOpenFilter={setOpenFilter}
            filterConditions={filterConditions}
            tableCells={headTableCells}
            setHeadTableCells={setHeadTableCells}
          />
        </div>
        <div className="table-container">
          <TableContainer>
            <Table aria-label="collapsible table" className={classes.table}>
              <TableHead>
                <TableRow>
                  {headTableCells.map((cell, index) => {
                    if (!cell.display) {
                      return null;
                    }

                    return (
                      <TableCell
                        key={index}
                        align="center"
                        className={`min-w-${cell.width}`}
                      >
                        {cell.sortField ? (
                          <TableSortLabel
                            active={sortField === cell.sortField}
                            IconComponent={sortDirection === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                            onClick={(event) => handleRequestSort(event, cell.sortField)}
                          >
                            {cell.label}
                          </TableSortLabel>
                        ) : (
                          cell.label
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {!depositsTotalCount ?
                  <TableRow>
                    <TableCell colSpan={Object.keys(headTableCells).length + 1} align="center">
                      No matching orders found.
                    </TableCell>
                  </TableRow>
                  :
                  depositsList.map((el) => (
                    <DepositsTableRow
                      key={el.order_id}
                      depositData={el}
                      headTableCells={headTableCells}
                    />
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>
          {!!depositsTotalCount &&
            <TablePagination
              rowsPerPageOptions={[10, 20, 40, 80, 100]}
              component="div"
              className={classes.paginationContainer}
              count={depositsTotalCount}
              rowsPerPage={rowsPerPage}
              page={currentPage - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        </div>
      </div>
      <Drawer
        anchor="right"
        open={openFilter}
        onClose={() => setOpenFilter((openFilter) => !openFilter)}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <DepositsFilterDrawer
          setOpenFilter={setOpenFilter}
          setFilterConditions={setFilterConditions}
          filterConditions={filterConditions}
        />
      </Drawer>
      <BackdropCircular loading={isDepositsLoading || loadingDepositConfig} />
    </div>
  );
};

export default Deposits;
